import Box from "@@/elements/Box";
import Picture from "@@/elements/Picture";
import Typography from "@@/elements/Typography";
import { ScreenSize, useIsBreakpointOrBelow } from "@@/hooks/device/use-breakpoint";
import OptimizelyBlock, { OptimizelyBlockProps } from "@@/integrations/optimizly/Block";
import EpiProperty from "@@/integrations/optimizly/Property";
import { LinkData } from "@@/models/linkdata";
import { PictureData } from "@@/models/pictureData";
import cn from "classnames";
import { CardColors } from "../CardProps";
import cardStyles from "../cardStyles.module.scss";
import styles from "./index.module.scss";

export type LinkCardProperties = {
  header: string;
  image?: PictureData;
};

export type LinkCardOwnProps = {
  link?: LinkData;
  color?: CardColors;
  fixedHeight?: boolean;
  anchorId?: string;
};

export type LinkCardProps = LinkCardOwnProps & LinkCardProperties;

export const LinkCard = ({
  header, image, color, link, fixedHeight, anchorId,
}: LinkCardProps) => {
  const variant = image ? "image" : "color";
  const classNames = cn(cardStyles.card, styles.link_card, cardStyles.card__link_card, {
    [(cardStyles as any)[`card_${color}`]]: color,
    [(styles as any)[`link_card_${variant}`]]: variant,
    [cardStyles.card__link]: link && link.href,
    [styles.link_card_fixed]: fixedHeight,
  });

  const Element = link && link.href ? "a" : "div";
  const screenSizeIsSmall = useIsBreakpointOrBelow(ScreenSize.SMALL);

  return (
    <>
      {image ? (
        <Element
          id={anchorId}
          href={link.href}
          target={link.target}
          title={link.title}
          className={classNames}
        >
          <Box
            width={["auto", "full"]}
            className={styles.link_card_img_container}
            flexGrow={[1, 1]}>
            <EpiProperty<LinkCardProperties> name="image">
              <Picture
                className="linkcard-image"
                imageData={image}
                aspectRatio={["1 / 1", "2 / 1", "2 / 1"]}
                sizes={[
                  [200, 400, 600, 800, 1200],
                  [200, 400, 600, 800, 1200],
                  [200, 400, 600, 800, 1200],
                ]}
              />
            </EpiProperty>
          </Box>
          <Box
            className={styles.link_card_title}
            py={[4, 4, 6]}
            px={[4, 4, 6]}
            width="full"
            height="full"
            color="Black"
          >
            <EpiProperty<LinkCardProperties> name="header">
              <Typography
                textcolor="dark"
                asElement="h3" 
                variant={screenSizeIsSmall ? "heading6" : "heading5"}>
                {header}
              </Typography>
            </EpiProperty>
          </Box>
        </Element>
      ) : (
        <Element
          id={anchorId}
          href={link.href}
          target={link.target}
          title={link.title}
          className={classNames}
        >
          <EpiProperty<LinkCardProperties> name="header">
            <Typography
              className={styles.link_card_title}
              asElement="h3"
              textcolor={color === "beige" ? "dark" : "light"}
              variant={screenSizeIsSmall ? "heading6" : "heading5"}
              p={[6, 6, 10]}
            >
              {header}
            </Typography>
          </EpiProperty>
        </Element>
      )}
    </>
  );
};

export type ProvidedLinkCardProps = OptimizelyBlockProps<LinkCardProperties> &
LinkCardProps;

const ProvidedLinkCard = ({
  blockId,
  inEditMode,
  properties,
  anchorId,
  ...rest
}: ProvidedLinkCardProps) => (
  <OptimizelyBlock
    blockId={blockId}
    inEditMode={inEditMode}
    properties={properties}
    anchorId={anchorId}
  >
    {(optimizlyProperties) => <LinkCard
      anchorId={anchorId}
      {...rest}
      {...optimizlyProperties} />}
  </OptimizelyBlock>
);

export default ProvidedLinkCard;
